/**
 * The motion vocabulary, for the places CSS cannot reach.
 *
 * These are the SAME four durations declared as custom properties at the top of
 * `index.css`; this module exists because a JS-driven animation (a step swap that
 * has to unmount the outgoing panel, a measured indicator) needs the number as a
 * number. `motion.test.ts` reads both and fails if they ever disagree, so there is
 * still exactly one source of truth.
 *
 * Anything expressible in CSS should use `var(--motion-*)` and never import this.
 *
 * The tiers are named for WHAT they answer, not for how long they are:
 *
 *   INTERACTION — "I heard you": hover, colour swap, icon nudge.
 *   PRESS       — the sharper beat of a press; deliberately quicker than the hover
 *                 so the two read as separate events rather than one long one.
 *   STATE       — a change the eye should follow: a tab indicator sliding, a
 *                 wizard step swapping, the theme icon turning over.
 *   SWEEP       — the travelling band's whole cycle: half pass, half parked
 *                 off-screen. One figure sets both the speed and the gap.
 */
export const MOTION = {
  interaction: 130,
  press: 80,
  state: 200,
  sweep: 4200,
} as const

/** The house easing: lands rather than drifts. */
export const EASE = 'cubic-bezier(0, 0, 0.2, 1)'

/** A step/page swap — longer tail, no overshoot. */
export const EASE_STEP = 'cubic-bezier(0.32, 0.72, 0, 1)'

/**
 * Whether the reader has asked for reduced motion, read at call time.
 *
 * For imperative code that must decide *now* (before starting a timer) rather than
 * re-render on a change; components should use `usePrefersReducedMotion`.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}
